import { useState } from 'react';
import {
  Globe, Mail, MessageCircle, Facebook, Twitter,
  Linkedin, Instagram, Youtube, Zap, Code, Key,
  Database, Cloud, Lock, Users, Bell, Clock,
  Calendar, BarChart, PieChart, Activity,
  CheckCircle, XCircle, RefreshCw, Save,
  Download, Upload, Trash2, Eye, EyeOff,
  Smartphone, Tablet, Laptop, Server,
  Wifi, Power, Settings as SettingsIcon,
  AlertTriangle, Info, HelpCircle
} from 'lucide-react';

export default function AppSettings() {
  const [activeTab, setActiveTab] = useState('integrations');
  const [saving, setSaving] = useState(false);
  const [showApiKey, setShowApiKey] = useState({});

  const [integrations, setIntegrations] = useState({
    email: {
      provider: 'smtp',
      smtp: { 
        host: 'smtp.gmail.com', 
        port: 587, 
        secure: true,
        user: 'contact@crmpro.com',
        password: '••••••••'
      },
      apiKey: 'sk_live_••••••••••••••••',
      webhook: 'https://api.crmpro.com/webhook/email'
    },
    social: {
      facebook: { 
        connected: true, 
        pageId: '123456789',
        accessToken: 'EA••••••••',
        webhook: 'https://api.crmpro.com/webhook/facebook'
      },
      twitter: { 
        connected: false,
        apiKey: '',
        apiSecret: ''
      },
      linkedin: { 
        connected: true, 
        companyId: '987654321',
        accessToken: 'AQ••••••••'
      },
      instagram: { 
        connected: false,
        businessId: ''
      }
    },
    crm: {
      hubspot: {
        connected: false,
        apiKey: '',
        portalId: ''
      },
      salesforce: {
        connected: false,
        clientId: '',
        clientSecret: ''
      },
      pipedrive: {
        connected: true,
        apiToken: '••••••••',
        companyDomain: 'crmpro'
      }
    },
    messaging: {
      slack: {
        connected: true,
        webhook: 'https://hooks.slack.com/services/•••',
        channel: '#crm-alerts'
      },
      telegram: {
        connected: false,
        botToken: '',
        chatId: ''
      },
      whatsapp: {
        connected: true,
        phoneNumberId: '123456789',
        accessToken: 'EA••••••'
      }
    },
    analytics: {
      googleAnalytics: {
        trackingId: 'UA-123456-78',
        viewId: '123456789',
        connected: true
      },
      mixpanel: {
        connected: false,
        token: ''
      },
      amplitude: {
        connected: false,
        apiKey: ''
      }
    },
    storage: {
      supabase: {
        url: 'https://fregvtiusgwmpodlvgfn.supabase.co',
        anonKey: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...',
        bucket: 'crm-files',
        region: 'eu-west-3'
      },
      aws: {
        connected: false,
        accessKeyId: '',
        secretAccessKey: '',
        bucket: ''
      }
    },
    webhooks: [
      {
        id: 1,
        name: 'Nouveau lead',
        url: 'https://api.example.com/webhook/lead',
        events: ['lead.created', 'lead.updated'],
        active: true
      },
      {
        id: 2,
        name: 'Deal gagné',
        url: 'https://api.example.com/webhook/deal',
        events: ['deal.won'],
        active: true
      },
      {
        id: 3,
        name: 'Appel enregistré',
        url: 'https://api.example.com/webhook/call',
        events: ['call.recorded'],
        active: false
      }
    ]
  });

  const tabs = [
    { id: 'integrations', label: 'Intégrations', icon: Globe },
    { id: 'email', label: 'Email', icon: Mail },
    { id: 'social', label: 'Réseaux sociaux', icon: Facebook },
    { id: 'crm', label: 'CRM externes', icon: Database },
    { id: 'messaging', label: 'Messagerie', icon: MessageCircle },
    { id: 'analytics', label: 'Analytics', icon: BarChart },
    { id: 'storage', label: 'Stockage', icon: Cloud },
    { id: 'webhooks', label: 'Webhooks', icon: Zap },
    { id: 'api', label: 'API', icon: Code }
  ];

  const handleSave = async () => {
    setSaving(true);
    await new Promise(resolve => setTimeout(resolve, 1500));
    setSaving(false);
  };

  const toggleApiKey = (key) => {
    setShowApiKey(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="space-y-6">
      {/* En-tête avec actions */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
            Paramètres application
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Gérez vos intégrations et connexions externes
          </p>
        </div>
        
        <div className="flex gap-2">
          <button className="px-4 py-2 bg-gray-900 rounded-lg border border-gray-800 text-gray-300 hover:text-white hover:border-gray-700 transition flex items-center gap-2">
            <Download size={18} />
            Exporter
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-lg text-white font-medium hover:shadow-lg hover:shadow-cyan-500/20 transition flex items-center gap-2 disabled:opacity-50"
          >
            {saving ? (
              <>
                <RefreshCw size={18} className="animate-spin" />
                Sauvegarde...
              </>
            ) : (
              <>
                <Save size={18} />
                Sauvegarder
              </>
            )}
          </button>
        </div>
      </div>

      {/* Tabs de navigation */}
      <div className="flex flex-wrap gap-2 border-b border-gray-800 pb-4">
        {tabs.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition flex items-center gap-2 ${
                activeTab === tab.id
                  ? 'bg-cyan-600 text-white'
                  : 'text-gray-400 hover:text-white hover:bg-gray-800'
              }`}
            >
              <Icon size={18} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Contenu des onglets */}
      <div className="bg-gradient-to-br from-gray-900 to-gray-950 rounded-xl border border-gray-800 p-6">
        {/* Intégrations */}
        {activeTab === 'integrations' && (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-white mb-4">Vue d'ensemble des intégrations</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Stats */}
              <div className="p-4 bg-gray-800/30 rounded-lg border border-gray-700">
                <p className="text-gray-400 text-sm">Intégrations actives</p>
                <p className="text-3xl font-bold text-white mt-1">8</p>
                <p className="text-xs text-green-400 mt-2">+2 ce mois</p>
              </div>
              <div className="p-4 bg-gray-800/30 rounded-lg border border-gray-700">
                <p className="text-gray-400 text-sm">Webhooks actifs</p>
                <p className="text-3xl font-bold text-white mt-1">3</p>
                <p className="text-xs text-blue-400 mt-2">12 requêtes/min</p>
              </div>
              <div className="p-4 bg-gray-800/30 rounded-lg border border-gray-700">
                <p className="text-gray-400 text-sm">API calls</p>
                <p className="text-3xl font-bold text-white mt-1">1.2k</p>
                <p className="text-xs text-purple-400 mt-2">Aujourd'hui</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              {/* Statut des services */}
              <div className="p-4 bg-gray-800/30 rounded-lg">
                <h3 className="text-white font-medium mb-3">Statut des services</h3>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Gmail</span>
                    <span className="text-green-400 text-sm flex items-center gap-1">
                      <CheckCircle size={12} /> Connecté
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Slack</span>
                    <span className="text-green-400 text-sm flex items-center gap-1">
                      <CheckCircle size={12} /> Connecté
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Facebook</span>
                    <span className="text-green-400 text-sm flex items-center gap-1">
                      <CheckCircle size={12} /> Connecté
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Twitter</span>
                    <span className="text-red-400 text-sm flex items-center gap-1">
                      <XCircle size={12} /> Déconnecté
                    </span>
                  </div>
                </div>
              </div>

              {/* Dernières activités */}
              <div className="p-4 bg-gray-800/30 rounded-lg">
                <h3 className="text-white font-medium mb-3">Dernières activités</h3>
                <div className="space-y-2">
                  <div className="text-sm">
                    <span className="text-cyan-400">15:30</span>
                    <span className="text-gray-300 ml-2">Webhook lead déclenché</span>
                  </div>
                  <div className="text-sm">
                    <span className="text-cyan-400">14:15</span>
                    <span className="text-gray-300 ml-2">Sync Gmail réussie</span>
                  </div>
                  <div className="text-sm">
                    <span className="text-cyan-400">11:20</span>
                    <span className="text-gray-300 ml-2">Message Slack envoyé</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Email */}
        {activeTab === 'email' && (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-white mb-4">Configuration Email</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Fournisseur SMTP</label>
                  <select className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white">
                    <option>Gmail</option>
                    <option>SendGrid</option>
                    <option>Mailgun</option>
                    <option>Custom SMTP</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Hôte SMTP</label>
                  <input 
                    type="text" 
                    value={integrations.email.smtp.host}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Port</label>
                    <input 
                      type="text" 
                      value={integrations.email.smtp.port}
                      className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Sécurité</label>
                    <select className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white">
                      <option>TLS</option>
                      <option>SSL</option>
                      <option>STARTTLS</option>
                    </select>
                  </div>
                </div>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Utilisateur</label>
                  <input 
                    type="text" 
                    value={integrations.email.smtp.user}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Mot de passe</label>
                  <div className="relative">
                    <input 
                      type={showApiKey.email ? 'text' : 'password'} 
                      value={integrations.email.smtp.password}
                      className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white pr-10"
                    />
                    <button 
                      onClick={() => toggleApiKey('email')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                    >
                      {showApiKey.email ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
                
                <div className="pt-4">
                  <button className="px-4 py-2 bg-green-600/20 text-green-400 rounded-lg hover:bg-green-600/30 transition">
                    Tester la connexion
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Réseaux sociaux */}
        {activeTab === 'social' && (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-white mb-4">Réseaux sociaux</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Facebook */}
              <div className="p-4 bg-gray-800/30 rounded-lg border border-gray-700">
                <div className="flex items-center gap-3 mb-3">
                  <Facebook className="text-blue-500" size={24} />
                  <h3 className="text-white font-medium">Facebook</h3>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-400 text-sm">Page ID</span>
                    <span className="text-white text-sm">{integrations.social.facebook.pageId}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400 text-sm">Statut</span>
                    <span className="text-green-400 text-sm flex items-center gap-1">
                      <CheckCircle size={12} /> Connecté
                    </span>
                  </div>
                </div>
              </div>

              {/* LinkedIn */}
              <div className="p-4 bg-gray-800/30 rounded-lg border border-gray-700">
                <div className="flex items-center gap-3 mb-3">
                  <Linkedin className="text-blue-600" size={24} />
                  <h3 className="text-white font-medium">LinkedIn</h3>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-400 text-sm">Company ID</span>
                    <span className="text-white text-sm">{integrations.social.linkedin.companyId}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400 text-sm">Statut</span>
                    <span className="text-green-400 text-sm flex items-center gap-1">
                      <CheckCircle size={12} /> Connecté
                    </span>
                  </div>
                </div>
              </div>

              {/* Twitter */}
              <div className="p-4 bg-gray-800/30 rounded-lg border border-gray-700">
                <div className="flex items-center gap-3 mb-3">
                  <Twitter className="text-sky-400" size={24} />
                  <h3 className="text-white font-medium">Twitter</h3>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-400 text-sm">API Key</span>
                    <span className="text-white text-sm">••••••••</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400 text-sm">Statut</span>
                    <span className="text-red-400 text-sm flex items-center gap-1">
                      <XCircle size={12} /> Déconnecté
                    </span>
                  </div>
                </div>
                <button className="mt-3 w-full px-3 py-2 bg-gray-800 rounded-lg text-sm text-sky-400 hover:bg-gray-700 transition">
                  Connecter
                </button>
              </div>

              {/* Instagram */}
              <div className="p-4 bg-gray-800/30 rounded-lg border border-gray-700">
                <div className="flex items-center gap-3 mb-3">
                  <Instagram className="text-pink-500" size={24} />
                  <h3 className="text-white font-medium">Instagram</h3>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-400 text-sm">Business ID</span>
                    <span className="text-white text-sm">Non configuré</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400 text-sm">Statut</span>
                    <span className="text-red-400 text-sm flex items-center gap-1">
                      <XCircle size={12} /> Déconnecté
                    </span>
                  </div>
                </div>
                <button className="mt-3 w-full px-3 py-2 bg-gray-800 rounded-lg text-sm text-pink-400 hover:bg-gray-700 transition">
                  Connecter
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Webhooks */}
        {activeTab === 'webhooks' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold text-white">Webhooks</h2>
              <button className="px-4 py-2 bg-cyan-600/20 text-cyan-400 rounded-lg hover:bg-cyan-600/30 transition flex items-center gap-2">
                <Zap size={18} />
                Nouveau webhook
              </button>
            </div>
            
            <div className="space-y-3">
              {integrations.webhooks.map(webhook => (
                <div key={webhook.id} className="p-4 bg-gray-800/30 rounded-lg border border-gray-700">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-white font-medium">{webhook.name}</h3>
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      webhook.active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                    }`}>
                      {webhook.active ? 'Actif' : 'Inactif'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-400 mb-2">{webhook.url}</p>
                  <div className="flex flex-wrap gap-2">
                    {webhook.events.map(event => (
                      <span key={event} className="px-2 py-1 bg-gray-700 rounded text-xs text-gray-300">
                        {event}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* API */}
        {activeTab === 'api' && (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-white mb-4">Configuration API</h2>
            
            <div className="p-4 bg-gray-800/30 rounded-lg border border-gray-700">
              <h3 className="text-white font-medium mb-3">Clés API</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">API Key</label>
                  <div className="relative">
                    <input 
                      type={showApiKey.main ? 'text' : 'password'} 
                      value="crm_live_••••••••••••••••••••"
                      className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white pr-10 font-mono"
                      readOnly
                    />
                    <button 
                      onClick={() => toggleApiKey('main')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                    >
                      {showApiKey.main ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm text-gray-400 mb-1">API Secret</label>
                  <div className="relative">
                    <input 
                      type={showApiKey.secret ? 'text' : 'password'} 
                      value="sk_live_••••••••••••••••••••"
                      className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white pr-10 font-mono"
                      readOnly
                    />
                    <button 
                      onClick={() => toggleApiKey('secret')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                    >
                      {showApiKey.secret ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="mt-4 p-3 bg-cyan-500/10 rounded-lg">
                <p className="text-sm text-cyan-400 flex items-center gap-2">
                  <Info size={16} />
                  Endpoint API : https://api.crmpro.com/v1
                </p>
              </div>
            </div>
            
            <div className="p-4 bg-gray-800/30 rounded-lg border border-gray-700">
              <h3 className="text-white font-medium mb-3">Rate Limiting</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-400">Requêtes/min</p>
                  <p className="text-xl font-bold text-white">60</p>
                </div>
                <div>
                  <p className="text-sm text-gray-400">Utilisation</p>
                  <p className="text-xl font-bold text-green-400">23%</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}